import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ConfigurationForm.css';
import { generatePassword, generateSecretKey, generateAppKey } from '../utils/passwordGenerator';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const REQUIRED_SERVICES = ['exolog', 'dealer', 'caddy'];
const OPTIONAL_SERVICES = [
  { id: 'mysql', label: 'MySQL', description: 'MySQL database' },
  { id: 'redis', label: 'Redis', description: 'Redis cache and queues' },
  { id: 'analytics', label: 'Analytics (Plausible)', description: 'Analytics system' },
  { id: 'codeserver', label: 'Code Server', description: 'VS Code in browser' },
  { id: 'phpmyadmin', label: 'phpMyAdmin', description: 'MySQL web interface' },
  { id: 'mailserver', label: 'Mail Server', description: 'Mail server' },
  { id: 'gitlab-runner', label: 'GitLab Runner', description: 'CI/CD runner' },
  { id: 'monitoring', label: 'Monitoring', description: 'Grafana, Prometheus, Node Exporter, cAdvisor' },
  { id: 'mirotalk', label: 'MiroTalk', description: 'WebRTC P2P video conferencing' },
  { id: 'libretranslate', label: 'LibreTranslate', description: 'Self-hosted machine translation service' },
  { id: 'gotenberg', label: 'Gotenberg', description: 'PDF generation service' },
  { id: 'altcha', label: 'ALTCHA Sentinel', description: 'ALTCHA challenge / captcha service' }
];

// Resource requirements for each service (CPU cores, RAM in GB, Disk in GB)
// Based on real-world usage - more realistic values
const SERVICE_REQUIREMENTS = {
  'exolog': { cpu: 1, ram: 1, disk: 20 },
  'dealer': { cpu: 0.5, ram: 0.5, disk: 10 },
  'caddy': { cpu: 0.25, ram: 0.25, disk: 1 },
  'mysql': { cpu: 1, ram: 1.5, disk: 50 },
  'redis': { cpu: 0.25, ram: 0.5, disk: 5 },
  'analytics': { cpu: 1, ram: 2, disk: 100 }, // Plausible + Postgres + ClickHouse
  'codeserver': { cpu: 0.5, ram: 1, disk: 10 },
  'phpmyadmin': { cpu: 0.25, ram: 0.25, disk: 1 },
  'mailserver': { cpu: 1, ram: 1.5, disk: 50 },
  'gitlab-runner': { cpu: 0.5, ram: 0.5, disk: 10 },
  'monitoring': { cpu: 1, ram: 1.5, disk: 30 }, // Prometheus + Grafana + exporters
  'mirotalk': { cpu: 0.5, ram: 0.5, disk: 5 },
  'libretranslate': { cpu: 1, ram: 1, disk: 10 },
  'gotenberg': { cpu: 1, ram: 1, disk: 10 },
  'altcha': { cpu: 0.5, ram: 0.5, disk: 5 }
};

// Calculate minimum resource requirements based on selected services
function calculateRequirements(services) {
  const requirements = { cpu: 0, ram: 0, disk: 0 };
  
  services.forEach(serviceId => {
    const req = SERVICE_REQUIREMENTS[serviceId];
    if (req) {
      requirements.cpu += req.cpu;
      requirements.ram += req.ram;
      requirements.disk += req.disk;
    }
  });
  
  // For CPU: no buffer, just round up and enforce minimum 4 cores
  const cpuBase = Math.ceil(requirements.cpu);
  const cpuFinal = Math.max(4, cpuBase || 0);

  // For RAM/Disk: add 20% buffer (reduced from 30% for more realistic values)
  const ramWithBuffer = Math.ceil(requirements.ram * 1.2);
  const diskWithBuffer = Math.ceil(requirements.disk * 1.2);

  return {
    cpu: cpuFinal,
    ram: ramWithBuffer || 0,
    disk: diskWithBuffer || 0
  };
}

// Filter server types based on requirements
function filterServerTypes(serverTypes, requirements) {
  if (!requirements || !serverTypes) return serverTypes;
  
  return serverTypes.filter(st => 
    st.cores >= requirements.cpu &&
    st.memory >= requirements.ram &&
    st.disk >= requirements.disk
  );
}

function ConfigurationForm({ config, serverTypes, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    domainPrefix: '',
    cloudflareZone: 'exolog.tech',
    serverType: '',
    datacenter: 'nbg1-dc3',
    image: 'ubuntu-24.04',
    services: [...REQUIRED_SERVICES],
    swapSize: '4',
    dockerUserSshPrivateKey: '',
    rootSshPrivateKey: '',
    developerSshPublicKeys: '',
    mysqlRootPassword: '',
    dbDatabase: 'exolog',
    dbUsername: 'exolog',
    dbPassword: '',
    gitRepo: '',
    gitBranch: '',
    appEnv: '',
    gitlabPersonalAccessToken: '',
    cloudflareApiToken: '',
    autoGeneratePasswords: true,
    manualServerSelection: false,
    altchaSecretKey: '',
    altchaRootPassword: '',
    posteEdition: 'free',
    posteRegistryUsername: '',
    posteRegistryPassword: '',
    ...config
  });

  const [errors, setErrors] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filteredServerTypes, setFilteredServerTypes] = useState(serverTypes || []);
  const [resourceRequirements, setResourceRequirements] = useState(null);

  useEffect(() => {
    if (config) {
      setFormData({
        domainPrefix: config.domainPrefix || '',
        cloudflareZone: config.cloudflareZone || 'exolog.tech',
        serverType: config.serverType || '',
        datacenter: config.datacenter || 'nbg1-dc3',
        image: config.image || 'ubuntu-24.04',
        services: config.services || [...REQUIRED_SERVICES],
        swapSize: config.swapSize || '4',
        dockerUserSshPrivateKey: config.dockerUserSshPrivateKey || '',
        rootSshPrivateKey: config.rootSshPrivateKey || '',
        developerSshPublicKeys: config.developerSshPublicKeys || '',
        mysqlRootPassword: config.mysqlRootPassword || '',
        dbDatabase: config.dbDatabase || 'exolog',
        dbUsername: config.dbUsername || 'exolog',
        dbPassword: config.dbPassword || '',
        gitRepo: config.gitRepo || '',
        gitBranch: config.gitBranch || '',
        appEnv: config.appEnv || '',
        gitlabPersonalAccessToken: config.gitlabPersonalAccessToken || '',
        cloudflareApiToken: config.cloudflareApiToken || '',
        autoGeneratePasswords: config.autoGeneratePasswords !== false,
        manualServerSelection: config.manualServerSelection || false,
        altchaSecretKey: config.altchaSecretKey || '',
        altchaRootPassword: config.altchaRootPassword || '',
        posteEdition: config.posteEdition || 'free',
        posteRegistryUsername: config.posteRegistryUsername || '',
        posteRegistryPassword: config.posteRegistryPassword || '',
        ...config
      });
    } else {
      // Auto-generate passwords for new config
      if (formData.autoGeneratePasswords) {
        generateAllPasswords();
      }
    }
  }, [config]);

  // Update filtered server types and requirements when services or selection mode change
  useEffect(() => {
    if (!formData.services || formData.services.length === 0) return;
    
    const requirements = calculateRequirements(formData.services);
    setResourceRequirements(requirements);

    if (formData.manualServerSelection) {
      // Manual mode: show all server types, no auto-clearing
      setFilteredServerTypes(serverTypes || []);
      return;
    }

    // Automatic mode: filter server types by requirements
    const filtered = filterServerTypes(serverTypes || [], requirements);
    setFilteredServerTypes(filtered);
    
    // If current server type doesn't meet requirements, clear it
    if (formData.serverType) {
      const currentServer = (serverTypes || []).find(st => st.name === formData.serverType);
      if (currentServer && (
        currentServer.cores < requirements.cpu ||
        currentServer.memory < requirements.ram ||
        currentServer.disk < requirements.disk
      )) {
        setFormData(prev => ({ ...prev, serverType: '' }));
      }
    }
  }, [formData.services, formData.serverType, formData.manualServerSelection, serverTypes]);

  const generateAllPasswords = () => {
    const newPasswords = {
      mysqlRootPassword: generatePassword(16),
      dbPassword: generatePassword(16),
      grafanaPassword: generatePassword(16),
      codeServerPassword: generatePassword(16),
      postgresPassword: generatePassword(16),
      plausibleSecretKeyBase: generateSecretKey(),
      totpVaultKey: generateSecretKey(),
      appKey: generateAppKey(),
      altchaSecretKey: generateSecretKey()
    };
    setFormData(prev => ({ ...prev, ...newPasswords }));
  };

  const handleGeneratePassword = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: generatePassword(16)
    }));
  };

  const handleGenerateAppEnv = async () => {
    try {
      const res = await axios.post(`${API_BASE}/generate-app-env`, { config: formData });
      if (res.data?.appEnv) {
        setFormData(prev => ({
          ...prev,
          appEnv: res.data.appEnv,
          ...(res.data.appKey != null && { appKey: res.data.appKey }),
          ...(res.data.posteApiPassword != null && { posteApiPassword: res.data.posteApiPassword })
        }));
      }
    } catch (err) {
      console.error('Failed to generate app env:', err);
      const message = err.response?.data?.error || err.response?.data?.details || err.message;
      alert(`Auto-generate failed: ${message}`);
    }
  };

  const handleServiceToggle = (serviceId) => {
    if (REQUIRED_SERVICES.includes(serviceId)) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.domainPrefix.trim()) {
      newErrors.domainPrefix = 'Domain prefix is required';
    }
    
    if (!formData.serverType) {
      newErrors.serverType = 'Please select server type';
    }
    
    // SSH private key is optional - if provided, public key will be generated
    // No validation needed

    if (formData.services.includes('exolog') && !formData.gitRepo.trim()) {
      newErrors.gitRepo = 'Git repository is required for Exolog';
    }

    if (formData.services.includes('mailserver') && formData.posteEdition === 'pro') {
      if (!(formData.posteRegistryUsername || '').trim()) {
        newErrors.posteRegistryUsername = 'Poste.io Pro requires a Docker registry username (poste.io account)';
      }
      if (!(formData.posteRegistryPassword || '').trim()) {
        newErrors.posteRegistryPassword = 'Poste.io Pro requires a Docker registry password';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave({
        ...formData,
        id: config?.id
      });
    }
  };

  return (
    <div className="configuration-form">
      <h2>{config ? 'Edit Configuration' : 'New Configuration'}</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>Services Selection</h3>
          <p className="section-description">
            Select the services you want to deploy. Required services: Exolog, Dealer, Caddy (enabled by default)
          </p>
          
          <div className="services-grid">
            {REQUIRED_SERVICES.map(serviceId => (
              <label key={serviceId} className="service-checkbox disabled">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                />
                <span className="service-label">
                  <strong>{serviceId}</strong>
                  <span className="service-badge">Required</span>
                </span>
              </label>
            ))}
            
            {OPTIONAL_SERVICES.map(service => (
              <label key={service.id} className="service-checkbox">
                <input
                  type="checkbox"
                  checked={formData.services.includes(service.id)}
                  onChange={() => handleServiceToggle(service.id)}
                />
                <span className="service-label">
                  <strong>{service.label}</strong>
                  <span className="service-description">{service.description}</span>
                </span>
              </label>
            ))}
          </div>

          {resourceRequirements && (
            <div className="requirements-summary" style={{ 
              marginTop: '20px', 
              padding: '15px', 
              backgroundColor: '#f3f4f6', 
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <strong>Minimum Server Requirements:</strong>
              <div style={{ marginTop: '8px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <span>CPU: <strong>{resourceRequirements.cpu} cores</strong></span>
                <span>RAM: <strong>{resourceRequirements.ram} GB</strong></span>
                <span>Disk: <strong>{resourceRequirements.disk} GB</strong></span>
              </div>
              {filteredServerTypes.length === 0 && (
                <div style={{ marginTop: '10px', color: '#dc2626', fontSize: '14px' }}>
                  ⚠️ No server types available that meet these requirements. Please deselect some services.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="form-section">
          <h3>Server Configuration</h3>
          
          <div className="form-group">
            <label className="checkbox-inline">
              <input
                type="checkbox"
                name="manualServerSelection"
                checked={!!formData.manualServerSelection}
                onChange={handleChange}
              />
              <span style={{ marginLeft: '8px' }}>
                Select server type manually (show all server types)
              </span>
            </label>
          </div>

          <div className="form-group">
            <label>
              Server Type <span className="required">*</span>
              <select
                name="serverType"
                value={formData.serverType}
                onChange={handleChange}
                className={errors.serverType ? 'error' : ''}
                disabled={filteredServerTypes.length === 0 && !formData.manualServerSelection}
              >
                <option value="">
                  {filteredServerTypes.length === 0 && !formData.manualServerSelection
                    ? 'No compatible servers (select fewer services)'
                    : 'Select server type'}
                </option>
                {filteredServerTypes.map(st => (
                  <option key={st.name} value={st.name}>
                    {st.name} - {st.cores} CPU, {st.memory}GB RAM, {st.disk}GB Disk (€{st.price}/mo)
                  </option>
                ))}
                {/* Show incompatible servers as disabled options */}
                {(serverTypes || []).filter(st => !filteredServerTypes.find(fst => fst.name === st.name)).map(st => (
                  <option key={st.name} value={st.name} disabled>
                    {st.name} - {st.cores} CPU, {st.memory}GB RAM, {st.disk}GB Disk (€{st.price}/mo) - Insufficient resources
                  </option>
                ))}
              </select>
              {errors.serverType && (
                <span className="error-message">{errors.serverType}</span>
              )}
            </label>
          </div>

          <div className="form-group">
            <label>
              Domain Prefix <span className="required">*</span>
              <input
                type="text"
                name="domainPrefix"
                value={formData.domainPrefix}
                onChange={handleChange}
                placeholder="dc3"
                className={errors.domainPrefix ? 'error' : ''}
              />
              {errors.domainPrefix && (
                <span className="error-message">{errors.domainPrefix}</span>
              )}
            </label>
          </div>

          <div className="form-group">
            <label>
              Cloudflare Zone
              <input
                type="text"
                name="cloudflareZone"
                value={formData.cloudflareZone}
                onChange={handleChange}
                placeholder="exolog.tech"
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Datacenter
              <select
                name="datacenter"
                value={formData.datacenter}
                onChange={handleChange}
              >
                <option value="nbg1-dc3">Nuremberg 1 DC3</option>
                <option value="nbg1-dc8">Nuremberg 1 DC8</option>
                <option value="fsn1-dc8">Falkenstein 1 DC8</option>
                <option value="hel1-dc2">Helsinki 1 DC2</option>
              </select>
            </label>
          </div>

          <div className="form-group">
            <label>
              OS Image
              <select
                name="image"
                value={formData.image}
                onChange={handleChange}
              >
                <option value="ubuntu-24.04">Ubuntu 24.04</option>
                <option value="ubuntu-22.04">Ubuntu 22.04</option>
              </select>
            </label>
          </div>

          <div className="form-group">
            <label>
              Swap Size (GB)
              <input
                type="number"
                name="swapSize"
                value={formData.swapSize}
                onChange={handleChange}
                min="1"
                max="16"
              />
            </label>
          </div>
        </div>


        <div className="form-section">
          <h3>SSH Configuration</h3>
          
          <div className="form-group">
            <label>
              Private SSH Key (docker_user) <span className="optional">(optional)</span>
              <textarea
                name="dockerUserSshPrivateKey"
                value={formData.dockerUserSshPrivateKey}
                onChange={handleChange}
                rows="8"
                placeholder="-----BEGIN OPENSSH PRIVATE KEY-----..."
                className={errors.dockerUserSshPrivateKey ? 'error' : ''}
              />
              {errors.dockerUserSshPrivateKey && (
                <span className="error-message">{errors.dockerUserSshPrivateKey}</span>
              )}
            </label>
          </div>

          <details style={{ marginTop: '15px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
              Advanced: Root SSH Key (for server access)
            </summary>
            <div className="form-group" style={{ marginTop: '10px' }}>
              <label>
                Private SSH Key (root access) <span className="optional">(optional)</span>
                <small style={{ display: 'block', color: '#666', marginBottom: '5px' }}>
                  If not provided, will use DC1 key from installer environment
                </small>
                <textarea
                  name="rootSshPrivateKey"
                  value={formData.rootSshPrivateKey}
                  onChange={handleChange}
                  rows="8"
                  placeholder="-----BEGIN OPENSSH PRIVATE KEY-----..."
                  className={errors.rootSshPrivateKey ? 'error' : ''}
                />
                {errors.rootSshPrivateKey && (
                  <span className="error-message">{errors.rootSshPrivateKey}</span>
                )}
              </label>
            </div>
          </details>

          <div className="form-group" style={{ marginTop: '15px' }}>
            <label>
              Developer SSH Public Keys (for docker_user@server access)
              <span className="optional">(optional)</span>
              <small style={{ display: 'block', color: '#666', marginTop: '4px' }}>
                Add public SSH keys for developers (one per line). These will be added to docker_user's authorized_keys.
              </small>
              <textarea
                name="developerSshPublicKeys"
                value={formData.developerSshPublicKeys}
                onChange={handleChange}
                rows="6"
                placeholder="ssh-rsa AAAAB3NzaC1yc2EAAAADAQABAAABAQC... user@example.com&#10;ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAI... developer@company.com"
                className={errors.developerSshPublicKeys ? 'error' : ''}
              />
              {errors.developerSshPublicKeys && (
                <span className="error-message">{errors.developerSshPublicKeys}</span>
              )}
            </label>
          </div>
        </div>

        {formData.services.includes('mysql') && (
          <div className="form-section">
            <h3>MySQL Settings</h3>
            
            <div className="form-group">
              <label>
                Root Password
                <div className="input-with-button">
                  <input
                    type="text"
                    name="mysqlRootPassword"
                    value={formData.mysqlRootPassword}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleGeneratePassword('mysqlRootPassword')}
                  >
                    Generate
                  </button>
                </div>
              </label>
            </div>

            <div className="form-group">
              <label>
                Database Name
                <input
                  type="text"
                  name="dbDatabase"
                  value={formData.dbDatabase}
                  onChange={handleChange}
                />
              </label>
            </div>

            <div className="form-group">
              <label>
                Database User
                <input
                  type="text"
                  name="dbUsername"
                  value={formData.dbUsername}
                  onChange={handleChange}
                />
              </label>
            </div>

            <div className="form-group">
              <label>
                Database Password
                <div className="input-with-button">
                  <input
                    type="password"
                    name="dbPassword"
                    value={formData.dbPassword}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleGeneratePassword('dbPassword')}
                  >
                    Generate
                  </button>
                </div>
              </label>
            </div>
          </div>
        )}

        {formData.services.includes('exolog') && (
          <div className="form-section">
            <h3>Exolog Settings</h3>
            
            <div className="form-group">
              <label>
                Git Repository <span className="required">*</span>
                <input
                  type="text"
                  name="gitRepo"
                  value={formData.gitRepo}
                  onChange={handleChange}
                  placeholder="git@gitlab.fidela.nl:fidela/exolog-reactJson.git"
                  className={errors.gitRepo ? 'error' : ''}
                />
                {errors.gitRepo && (
                  <span className="error-message">{errors.gitRepo}</span>
                )}
              </label>
            </div>

            <div className="form-group">
              <label>
                Git Branch
                <input
                  type="text"
                  name="gitBranch"
                  value={formData.gitBranch}
                  onChange={handleChange}
                  placeholder="main"
                />
              </label>
            </div>

            <div className="form-group">
              <label>
                GitLab Personal Access Token
                <input
                  type="password"
                  name="gitlabPersonalAccessToken"
                  value={formData.gitlabPersonalAccessToken}
                  onChange={handleChange}
                  placeholder="glpat-..."
                />
                <small className="form-hint">
                  GitLab personal access token for API access
                </small>
              </label>
            </div>

            <div className="form-group">
              <label>
                Cloudflare API Token
                <input
                  type="password"
                  name="cloudflareApiToken"
                  value={formData.cloudflareApiToken}
                  onChange={handleChange}
                  placeholder="Cloudflare API token"
                />
                <small className="form-hint">
                  Cloudflare API token for DNS management
                </small>
              </label>
            </div>

            {formData.services.includes('mailserver') && (
              <>
                <div className="form-group">
                  <label>
                    Poste.io edition
                    <select
                      name="posteEdition"
                      value={formData.posteEdition || 'free'}
                      onChange={handleChange}
                    >
                      <option value="free">Free (Docker Hub: analogic/poste.io)</option>
                      <option value="pro">Pro (private registry: poste.io/mailserver)</option>
                    </select>
                    <small className="form-hint">
                      Pro requires <code>docker login</code> to https://poste.io before pulling the image (
                      <a href="https://poste.io/doc/getting-started#download" target="_blank" rel="noopener noreferrer">
                        documentation
                      </a>
                      ). The installer runs login on the server before <code>docker compose up</code>.
                    </small>
                  </label>
                </div>

                {formData.posteEdition === 'pro' && (
                  <>
                    <div className="form-group">
                      <label>
                        Poste.io registry username
                        <input
                          type="text"
                          name="posteRegistryUsername"
                          value={formData.posteRegistryUsername || ''}
                          onChange={handleChange}
                          placeholder="poste.io account login"
                          className={errors.posteRegistryUsername ? 'input-error' : ''}
                        />
                        {errors.posteRegistryUsername && (
                          <span className="error-text">{errors.posteRegistryUsername}</span>
                        )}
                      </label>
                    </div>
                    <div className="form-group">
                      <label>
                        Poste.io registry password
                        <input
                          type="password"
                          name="posteRegistryPassword"
                          value={formData.posteRegistryPassword || ''}
                          onChange={handleChange}
                          placeholder="poste.io account password"
                          className={errors.posteRegistryPassword ? 'input-error' : ''}
                        />
                        {errors.posteRegistryPassword && (
                          <span className="error-text">{errors.posteRegistryPassword}</span>
                        )}
                      </label>
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>
                    Poste API Password
                    <div className="input-with-button">
                      <input
                        type="password"
                        name="posteApiPassword"
                        value={formData.posteApiPassword || ''}
                        onChange={handleChange}
                        placeholder="Auto-generated when you click Auto-generate"
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleGeneratePassword('posteApiPassword')}
                      >
                        Generate
                      </button>
                    </div>
                    <small className="form-hint">
                      Password for Poste.io admin API (admin@mail.domain). Used in app .env as POSTE_API_PASSWORD.
                    </small>
                  </label>
                </div>
              </>
            )}

            <div className="form-group">
              <label>
                App .env File
                <div className="textarea-with-button">
                  <textarea
                    name="appEnv"
                    value={formData.appEnv}
                    onChange={handleChange}
                    rows="15"
                    placeholder="APP_NAME=exolog&#10;APP_ENV=production&#10;..."
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={handleGenerateAppEnv}
                  >
                    Auto-generate
                  </button>
                </div>
                <small className="form-hint">
                  Click "Auto-generate" to create a pre-filled .env file based on your configuration
                </small>
              </label>
            </div>
          </div>
        )}

        {formData.services.includes('monitoring') && (
          <div className="form-section">
            <h3>Monitoring Settings</h3>
            
            <div className="form-group">
              <label>
                Grafana Password
                <div className="input-with-button">
                  <input
                    type="text"
                    name="grafanaPassword"
                    value={formData.grafanaPassword || ''}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleGeneratePassword('grafanaPassword')}
                  >
                    Generate
                  </button>
                </div>
              </label>
            </div>
          </div>
        )}

        {formData.services.includes('codeserver') && (
          <div className="form-section">
            <h3>Code Server Settings</h3>
            
            <div className="form-group">
              <label>
                Code Server Password
                <div className="input-with-button">
                  <input
                    type="text"
                    name="codeServerPassword"
                    value={formData.codeServerPassword || ''}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleGeneratePassword('codeServerPassword')}
                  >
                    Generate
                  </button>
                </div>
              </label>
            </div>
          </div>
        )}

        {formData.services.includes('analytics') && (
          <div className="form-section">
            <h3>Plausible Analytics Settings</h3>
            
            <div className="form-group">
              <label>
                PostgreSQL Password
                <div className="input-with-button">
                  <input
                    type="text"
                    name="postgresPassword"
                    value={formData.postgresPassword || ''}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleGeneratePassword('postgresPassword')}
                  >
                    Generate
                  </button>
                </div>
              </label>
            </div>
          </div>
        )}

        {formData.services.includes('mirotalk') && (
          <div className="form-section">
            <h3>MiroTalk Settings</h3>
            
            <div className="form-group">
              <label>
                JWT Secret Key
                <div className="input-with-button">
                  <input
                    type="text"
                    name="mirotalkJwtKey"
                    value={formData.mirotalkJwtKey || ''}
                    onChange={handleChange}
                    placeholder="Auto-generated if empty"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      const key = generateSecretKey();
                      setFormData(prev => ({ ...prev, mirotalkJwtKey: key }));
                    }}
                  >
                    Generate
                  </button>
                </div>
                <small className="form-hint">
                  JWT key for MiroTalk API authentication. Leave empty to auto-generate.
                </small>
              </label>
            </div>
          </div>
        )}

        {formData.services.includes('altcha') && (
          <div className="form-section">
            <h3>ALTCHA Sentinel</h3>

            <div className="form-group">
              <label>
                HMAC secret (ALTCHA_SECRET_KEY)
                <div className="input-with-button">
                  <input
                    type="text"
                    name="altchaSecretKey"
                    value={formData.altchaSecretKey || ''}
                    onChange={handleChange}
                    placeholder="Auto-generated on deploy if empty"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      const key = generateSecretKey();
                      setFormData(prev => ({ ...prev, altchaSecretKey: key }));
                    }}
                  >
                    Generate
                  </button>
                </div>
                <small className="form-hint">
                  Used as ALTCHA_HMAC_KEY on the server. Leave empty to auto-generate when Terraform files are built.
                </small>
              </label>
            </div>

            <div className="form-group">
              <label>
                Root password (optional)
                <div className="input-with-button">
                  <input
                    type="text"
                    name="altchaRootPassword"
                    value={formData.altchaRootPassword || ''}
                    onChange={handleChange}
                    placeholder="Leave empty to use the image default"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleGeneratePassword('altchaRootPassword')}
                  >
                    Generate
                  </button>
                </div>
                <small className="form-hint">
                  Sets DEFAULT_ROOT_PASSWORD only when filled. Otherwise the Sentinel image keeps its default root user password.
                </small>
              </label>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

export default ConfigurationForm;
